// File download/view helpers for private S3 documents.
// All app docs are stored in a private S3 bucket — direct URLs return AccessDenied.
// These helpers fetch a presigned URL from the backend, then download/view via blob.
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Fetch a short-lived presigned URL from the backend.
 * @param {string} api - REACT_APP_BACKEND_URL/api
 * @param {string} endpoint - e.g. `/drivers/{id}/file-url/driver_photo`
 * @returns presigned URL string
 */
export const fetchPresignedUrl = async (api, endpoint) => {
  const res = await axios.get(`${api}${endpoint}`);
  return res.data?.url;
};

/**
 * Opens a private S3 document in a new tab using a presigned URL.
 */
export const viewSignedDocument = async (api, endpoint) => {
  try {
    const url = await fetchPresignedUrl(api, endpoint);
    if (!url) throw new Error('Empty presigned URL');
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    toast.error(err?.response?.data?.detail || 'Could not open document');
  }
};

/**
 * Downloads a private S3 document to the user's device using a presigned URL.
 */
export const downloadSignedDocument = async (api, endpoint, filename) => {
  try {
    const presigned = await fetchPresignedUrl(api, endpoint);
    if (!presigned) throw new Error('Empty presigned URL');
    const resp = await fetch(presigned);
    if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
    const blob = await resp.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename || extractFilename(presigned));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    toast.success('Download started');
  } catch (err) {
    // Last-ditch: open in new tab so user can save manually
    try {
      const presigned = await fetchPresignedUrl(api, endpoint);
      if (presigned) {
        window.open(presigned, '_blank', 'noopener,noreferrer');
        toast.info('Opened document in a new tab — use browser save option');
        return;
      }
    } catch { /* ignore */ }
    toast.error('Download failed');
  }
};

const extractFilename = (url) => {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last || 'document';
  } catch {
    return 'document';
  }
};

// Legacy helper kept for compat with any caller still using direct public URLs.
// Prefer downloadSignedDocument() for private S3 docs.
export const downloadDocument = async (url, filename) => {
  if (!url) {
    toast.error('No document available');
    return;
  }
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response not OK');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename || extractFilename(url));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    toast.success('Download started');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.info('Opened document in a new tab — use browser save option');
  }
};
